/**
 * The app shell: topbar, always-visible sync pill, the router, and — on every
 * survey route — the Survey Map (D-028).
 *
 * Two shape decisions worth knowing about.
 *
 * **The sync pill lives in the shell, not on a screen.** Brief §2 says it is
 * always visible; putting it in the topbar is what makes that true on every
 * screen including ones nobody has written yet.
 *
 * **Navigation is the Survey Map.** Since 2026-08-30 (D-028) the map bar is
 * docked on every survey screen and opens a sheet that reaches any section in
 * one tap; the linear walk (Previous / Close & next) lives at the end of each
 * section's form. The breadcrumb, the hall indicator route's old chrome, and
 * the sticky per-screen navbars are gone — the bar is the way up and out.
 */

import { useEffect, useRef, type ReactNode } from 'react';

import { SurveyMap, SyncPill } from './components';
import { hrefFor, type Route } from './routing';
import { useRoute } from './useRoute';
import { ServicesProvider, useServices, type AppServices } from './state/services';
import { SurveyProvider } from './state/SurveyProvider';
import { useSyncStatus } from './state/useSyncStatus';
import { markWalked, walkedSectionForRoute } from './state/walked';
import { useViewportGlue } from './state/viewportGlue';
import { AccessScreen } from './screens/AccessScreen';
import { AdminScreen } from './screens/AdminScreen';
import { AttentionScreen } from './screens/AttentionScreen';
import { sectionInstanceId } from '../core/types';
import { BuildingScreen } from './screens/BuildingScreen';
import { CloseScreen } from './screens/CloseScreen';
import { DeviceScreen } from './screens/DeviceScreen';
import { HomeScreen } from './screens/HomeScreen';
import { OnboardScreen } from './screens/OnboardScreen';
import { RoomSetupScreen } from './screens/RoomSetupScreen';
import { SectionScreen } from './screens/SectionScreen';
import { SectionsScreen } from './screens/SectionsScreen';

export interface AppProps {
  /** Built once in src/main.tsx. Never rebuilt, never constructed in a screen. */
  services: AppServices;
}

export function App({ services }: AppProps): ReactNode {
  return (
    <ServicesProvider services={services}>
      <Shell />
    </ServicesProvider>
  );
}

function Shell(): ReactNode {
  const route = useRoute();
  const { sync, linked } = useServices();
  const status = useSyncStatus(sync);
  const href = hrefFor(route);
  const topbar = useRef<HTMLElement | null>(null);

  // A new screen starts at the top. Without this a surveyor who scrolled to the
  // bottom of Electrical lands halfway down Pit.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [href]);

  /*
   * Record that the surveyor has actually been shown this part of the job.
   *
   * It lives in the SHELL rather than on the screens for the same reason the
   * sync pill does: it has to be true of every route that renders a section,
   * including the two that do not go through `SectionScreen` — room setup has
   * its own screen, and the building section is rendered by the building
   * screen. A per-screen effect is a rule three files have to remember; this is
   * one line the router already runs on every navigation.
   *
   * What it feeds is `guide.ts`'s submit gate — "has this surveyor seen machine
   * room 2 at all?" — and nothing else. It is never survey data; see walked.ts.
   */
  useEffect(() => {
    const visit = walkedSectionForRoute(route);
    if (visit) markWalked(visit.surveyId, visit.sectionId);
  }, [route]);

  /*
   * Publish the topbar's rendered height as `--topbar-h`.
   *
   * A screen that wants to pin something DIRECTLY UNDER the topbar needs a
   * number (nothing does today; the mechanism is kept), and the topbar
   * has no fixed one: it grows when the breadcrumb wraps to two lines on a long
   * room name, and it grows again by the notch inset on an iPhone. Measuring is
   * the only honest source; a hard-coded 96px is a bar that overlaps the
   * wordmark on one phone and floats below it on another.
   */
  useEffect(() => {
    const node = topbar.current;
    if (!node) return undefined;

    const publish = (): void => {
      const height = Math.round(node.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--topbar-h', `${String(height)}px`);
    };

    publish();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, []);

  /*
   * Keep the map bar, sheet, veil and toast glued to the VISUAL viewport
   * (round 8 task 2, the field bug). Same shape as the `--topbar-h`
   * publisher above — one hook, mounted once here rather than in MapBar or
   * MapSheet, which `tests/unit/map-chrome.test.ts` renders with no
   * `window`. See `src/ui/state/viewportGlue.ts` for the why.
   */
  useViewportGlue();

  const surveyId = surveyIdOf(route);

  return (
    <div className={surveyId ? 'app app--mapped' : 'app'}>
      <header className="topbar" ref={topbar}>
        <div className="topbar__brand">
          {/*
            * The illuminated Upsurv logo (brand round, 2026-09-06): a raster,
            * not text, because the approved artwork is rendered light — a
            * luminous amber U with its elevator arrow inside a soft halo — and
            * no CSS shadow reproduces it. The link keeps its accessible name
            * ("UpSurv" — six e2e specs find home by it) and its 44px floor;
            * the picture is decorative to a screen reader (alt="") because the
            * link already says the word. 1×/2×/3× so a phone draws it sharp
            * at 128 × 44 CSS px; theme.css picks the 96px cut under 360px so
            * the sync pill keeps its words.
            */}
          <a className="topbar__wordmark" href={hrefFor({ name: 'home' })} aria-label="UpSurv">
            <img
              className="topbar__logo"
              src="/brand/upsurv-glow-128-v2.png"
              srcSet="/brand/upsurv-glow-128-v2.png 1x, /brand/upsurv-glow-128@2x-v2.png 2x, /brand/upsurv-glow-128@3x-v2.png 3x"
              width={128}
              height={44}
              alt=""
              decoding="async"
            />
          </a>
          <SyncPill status={status} linked={linked} />
        </div>
      </header>

      <main className="screen">
        <Screen route={route} />
      </main>
    </div>
  );
}

/**
 * The routes the Survey Map is docked on — every screen of an open survey
 * (brief §2: "present on every screen"). Home, admin, access, onboarding, the
 * device page and the needs-you list are not inside a survey and get no bar.
 */
function surveyIdOf(route: Route): string | null {
  switch (route.name) {
    case 'building':
    case 'roomSetup':
    case 'sections':
    case 'section':
    case 'submit':
      return route.surveyId;
    default:
      return null;
  }
}

/**
 * The route table. Every survey route is wrapped in one `SurveyProvider`, which
 * is what guarantees a screen can never reach the store or an engine directly.
 */
function Screen({ route }: { route: Route }): ReactNode {
  switch (route.name) {
    case 'home':
      return <HomeScreen />;

    case 'admin':
      return <AdminScreen />;

    case 'access':
      return <AccessScreen />;

    case 'onboard':
      return <OnboardScreen />;

    case 'device':
      return <DeviceScreen />;

    case 'attention':
      return <AttentionScreen />;

    case 'building':
      return (
        <SurveyProvider surveyId={route.surveyId}>
          <BuildingScreen surveyId={route.surveyId} />
          <SurveyMap />
        </SurveyProvider>
      );

    case 'roomSetup':
      return (
        <SurveyProvider surveyId={route.surveyId}>
          <RoomSetupScreen surveyId={route.surveyId} roomId={route.roomId} />
          <SurveyMap />
        </SurveyProvider>
      );

    case 'sections':
      return (
        <SurveyProvider surveyId={route.surveyId}>
          <SectionsScreen surveyId={route.surveyId} roomId={route.roomId} />
          <SurveyMap />
        </SurveyProvider>
      );

    case 'section':
      // The building SECTION renders the Building screen (D-031): the walk's
      // "Previous: Building" lands on `#/section/building`, and rendering the
      // catalog generically there resurrected the chip header the Building
      // screen itself lost — two renderings of one place, one of them stale.
      // Same one-section-one-screen move RoomSetupScreen made on 2026-08-18.
      if (route.sectionId === sectionInstanceId('building')) {
        return (
          <SurveyProvider surveyId={route.surveyId}>
            <BuildingScreen surveyId={route.surveyId} />
            <SurveyMap />
          </SurveyProvider>
        );
      }
      return (
        <SurveyProvider surveyId={route.surveyId}>
          <SectionScreen surveyId={route.surveyId} sectionId={route.sectionId} />
          <SurveyMap />
        </SurveyProvider>
      );

    case 'submit':
      return (
        <SurveyProvider surveyId={route.surveyId}>
          <CloseScreen surveyId={route.surveyId} />
          <SurveyMap />
        </SurveyProvider>
      );

    case 'notFound':
      return (
        <>
          <p className="empty">
            No screen at <code>{route.path}</code>.
          </p>
          <a className="btn btn--ghost" href={hrefFor({ name: 'home' })}>
            My surveys
          </a>
        </>
      );
  }
}
